<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import type { Project, ProjectStatus } from '../lib/types';
  import { draggedProject, dropTarget, requestProjectMove } from '../lib/stores/ui';
  import ProjectCard from './ProjectCard.svelte';

  interface Props {
    monthKey: string;
    projects: Project[];
    maxVisible?: number;
  }

  let { monthKey, projects, maxVisible = 5 }: Props = $props();

  let isDropTarget = $state(false);
  let isExpanded = $state(false);

  let hasMore = $derived(projects.length > maxVisible);
  let visibleProjects = $derived(isExpanded ? projects : projects.slice(0, maxVisible));
  let hiddenCount = $derived(projects.length - maxVisible);

  function handleDragOver(e: DragEvent) {
    e.preventDefault();
    if ($draggedProject) {
      isDropTarget = true;
      dropTarget.set({ monthKey, status: 'done' });
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      isDropTarget = false;
      dropTarget.set(null);
    }
  }

  function handleDrop(e: DragEvent) {
    e.preventDefault();
    isDropTarget = false;
    dropTarget.set(null);

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'month', { monthKey, status: 'done' });
    }
  }

  function toggleExpanded() {
    isExpanded = !isExpanded;
  }
</script>

<div
  class="done-column bg-green-50/70 transition-all"
  class:ring-2={isDropTarget}
  class:ring-blue-400={isDropTarget}
  class:bg-blue-100={isDropTarget}
  ondragover={handleDragOver}
  ondragleave={handleDragLeave}
  ondrop={handleDrop}
  role="list"
>
  <!-- Column header (inside the green background) -->
  <div class="mb-1.5 flex items-center gap-1.5">
    <span class="text-sm">✅</span>
    <h4 class="text-xs font-semibold tracking-wide text-gray-600 uppercase">Done</h4>
    <span class="rounded-full bg-gray-200/70 px-1 py-0.5 text-[10px] text-gray-400">
      {projects.length}
    </span>
  </div>

  <!-- Cards -->
  {#if projects.length === 0}
    <div class="py-4 text-center text-xs text-gray-400">Drop completed here</div>
  {:else}
    <div class="flex flex-col gap-1.5">
      {#each visibleProjects as project (project.id)}
        <ProjectCard {project} compact clickable />
      {/each}
    </div>

    <!-- Show more / less toggle -->
    {#if hasMore}
      <button class="show-more-btn" onclick={toggleExpanded}>
        {#if isExpanded}
          <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 15l7-7 7 7"
            />
          </svg>
          Show less
        {:else}
          <svg class="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
          +{hiddenCount} more
        {/if}
      </button>
    {/if}
  {/if}
</div>

<style>
  .done-column {
    display: flex;
    flex-direction: column;
    min-width: 160px;
    max-width: 180px;
    flex-shrink: 0;
    padding: 0.5rem;
    border-radius: 0.375rem;
    min-height: 3.5rem;
  }

  .show-more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.25rem;
    width: 100%;
    margin-top: 0.375rem;
    padding: 0.375rem;
    font-size: 0.625rem;
    font-weight: 500;
    color: #6b7280;
    background: rgba(255, 255, 255, 0.6);
    border: 1px dashed #d1d5db;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .show-more-btn:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: #9ca3af;
    color: #374151;
  }
</style>
